import React, { HTMLAttributes, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import cx from "classnames";
import { Util } from "@xt/xtbase";

import store from "store";
import { getUpDownCls, getOneByThreeStatus, routerPush } from "@/utils/method";
import useUpDownClassTicker from "@/hooks/useUpDownClassTicker";
import useFormatBigNumber from "@/hooks/useFormatBigNumber";
import XtFontScale from "@/components/Xt/fontScale";
import CMPT_Star from "../star";

import styles from "./index.module.scss";

import { TickerProps } from "store/trade";
import { LeverSymbolProps } from "store/market";

const { Big } = Util;

interface Props extends HTMLAttributes<HTMLDivElement> {
  ticker: TickerProps;
  isShowVolume: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

const Main: React.FC<Props> = ({ className, ticker, isShowVolume, disabled, onClick, ...rest }) => {
  const router = useRouter();
  const { name, leverConfigObj, config } = store.market;

  const isCfd = useMemo(() => {
    const byRoute = router.pathname?.startsWith("/futures/") || router.asPath?.startsWith("/futures/");
    const byStore = !!store.market.name && store.market.name.toLowerCase().endsWith("_cfd");
    return byRoute || byStore;
  }, [router.pathname, router.asPath, store.market.name]);

  const volumePrecision = useMemo(() => {
    if (!config) return undefined;
    const cfg = config[ticker.s];
    if (!cfg) return undefined;
    return cfg.quantityPrecision && cfg.quantityPrecision >= 0 ? cfg.quantityPrecision : 0;
  }, [ticker, config]);
  const volume = useFormatBigNumber(ticker ? ticker.q || 0 : 0, volumePrecision);

  const coinQuantity = useMemo<string>(() => ticker.s.split("_")[0].toUpperCase(), [ticker]);
  const coinPrice = useMemo<string>(() => ticker.s.split("_")[1].toUpperCase(), [ticker]);
  const leverDoc = useMemo<WithUndefined<LeverSymbolProps>>(() => {
    if (!leverConfigObj) return;
    return leverConfigObj[ticker.s];
  }, [ticker, leverConfigObj]);

  const priceCls = useUpDownClassTicker(ticker);
  const priceLab = useMemo(() => {
    let precision = 2;
    if (config && config[ticker.s] && config[ticker.s].pricePrecision && config[ticker.s].pricePrecision >= 0) {
      precision = config[ticker.s].pricePrecision;
    }
    return Big(ticker.c || 0).toFixedCy(precision);
  }, [ticker, config]);

  const rateLab = useMemo<string>(() => {
    const rate = ticker.cr || "0";
    const sign = getOneByThreeStatus({ gt: "+", eq: "", lt: "" }, rate);
    const val = Big(rate).times(100).toFixed(2);
    return sign + val + "%";
  }, [ticker]);

  const handleClick = () => {
    if (disabled) return;
    routerPush(router, { symbol: ticker.s, isCfd });
    onClick && onClick();
  };

  const handleClickTag = (e) => {
    e && e.stopPropagation();
    routerPush(router, { symbol: ticker.s, isLever: true });
  };

  return (
    <div onClick={handleClick} className={cx(styles.main, { [styles.atv]: name === ticker.s }, className)} {...rest}>
      <div className={styles.name}>
        <CMPT_Star symbol={ticker.s} />
        <div className={styles.nameDiv}>
          <div>
            <b>{coinQuantity}</b>
            <span>{"/" + coinPrice}</span>
            {!!leverDoc && (
              <button onClick={handleClickTag} className={cx("btnTxt", styles.tag)}>
                {leverDoc.maxLeverage + "X"}
              </button>
            )}
          </div>
        </div>
      </div>
      <XtFontScale className={cx(styles.price, priceCls)}>{priceLab}</XtFontScale>
      {isShowVolume ? (
        <XtFontScale>{volume}</XtFontScale>
      ) : (
        <XtFontScale className={cx(styles.rate, getUpDownCls(ticker.cr))}>{rateLab}</XtFontScale>
      )}
    </div>
  );
};

export default observer(Main);
